import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Share2, Bookmark, Sparkles, ThumbsUp, ThumbsDown, ExternalLink } from 'lucide-react';
import api from '@/api';
import openExternal from '@/utils/openExternal';

const CARD_TABS = [
  { key: 'what', label: 'WHAT', className: 'exp-card-what' },
  { key: 'why', label: 'WHY', className: 'exp-card-why' },
  { key: 'matters', label: 'YOU', className: 'exp-card-matters' },
];

const CARD_LABELS = {
  what: 'WHAT HAPPENED',
  why: 'WHY IT HAPPENED',
  matters: 'WHY IT MATTERS TO YOU',
};

const LABEL_COLORS = {
  what: { bg: 'rgba(255,255,255,0.2)', text: '#FFFFFF', dot: '#FBBF24' },
  why: { bg: 'rgba(255,255,255,0.2)', text: '#FFFFFF', dot: '#FFFFFF' },
  matters: { bg: 'rgba(255,255,255,0.2)', text: '#FFFFFF', dot: '#FFFFFF' },
};

const CARD_3D_ACCENT = {
  what: '#93C5FD',
  why: '#FCD34D',
  matters: '#6EE7B7',
};

const CATEGORY_CONFIG = {
  finance: { label: 'MARKETS', color: '#3B82F6', emoji: '\uD83D\uDCB0' },
  technology: { label: 'TECH', color: '#10B981', emoji: '\u26A1' },
  ai: { label: 'AI', color: '#6B7280', emoji: '\uD83E\uDD16' },
  economy: { label: 'ECONOMY', color: '#F59E0B', emoji: '\uD83D\uDCCA' },
  crypto: { label: 'CRYPTO', color: '#F97316', emoji: '\u20BF' },
  science: { label: 'SCIENCE', color: '#8B5CF6', emoji: '\uD83D\uDD2C' },
  world_news: { label: 'WORLD', color: '#EF4444', emoji: '\uD83D\uDD25' },
  internet_culture: { label: 'CULTURE', color: '#F43F5E', emoji: '\uD83C\uDF10' },
  politics: { label: 'POLITICS', color: '#DC2626', emoji: '\uD83C\uDFDB\uFE0F' },
  entertainment: { label: 'CELEB', color: '#EC4899', emoji: '\uD83C\uDFAC' },
  lifestyle: { label: 'STYLE', color: '#A855F7', emoji: '\uD83D\uDC85' },
  sports: { label: 'SPORTS', color: '#16A34A', emoji: '\uD83C\uDFC0' },
  sports_betting: { label: 'SPORTS BETS', color: '#16A34A', emoji: '\uD83C\uDFB2' },
  fantasy_sports: { label: 'FANTASY', color: '#059669', emoji: '\uD83C\uDFC8' },
  options_trading: { label: 'OPTIONS', color: '#2563EB', emoji: '\uD83D\uDCC8' },
  forex: { label: 'FOREX', color: '#0891B2', emoji: '\uD83D\uDCB1' },
  commodities: { label: 'COMMODITIES', color: '#D97706', emoji: '\uD83D\uDEE2\uFE0F' },
  stocks: { label: 'STOCKS', color: '#1D4ED8', emoji: '\uD83D\uDCC9' },
  ecommerce: { label: 'E-COMMERCE', color: '#7C3AED', emoji: '\uD83D\uDED2' },
  dropshipping: { label: 'DROPSHIP', color: '#9333EA', emoji: '\uD83D\uDCE6' },
  sneakers_streetwear: { label: 'SNEAKERS', color: '#E11D48', emoji: '\uD83D\uDC5F' },
  fashion_luxury: { label: 'LUXURY', color: '#BE185D', emoji: '\uD83D\uDC5C' },
  trading_cards: { label: 'CARDS', color: '#B45309', emoji: '\uD83C\uDCCF' },
  gaming_items: { label: 'GAMING', color: '#4F46E5', emoji: '\uD83C\uDFAE' },
  collectibles: { label: 'COLLECT', color: '#9F1239', emoji: '\uD83D\uDDDD\uFE0F' },
  toys_popculture: { label: 'POP CULTURE', color: '#DB2777', emoji: '\uD83E\uDDF8' },
  small_business: { label: 'BUSINESS', color: '#6D28D9', emoji: '\uD83C\uDFEA' },
  property: { label: 'PROPERTY', color: '#0D9488', emoji: '\uD83C\uDFE0' },
  tiktok_trending: { label: 'TIKTOK', color: '#111827', emoji: '\uD83C\uDFB5' },
  shopify_trends: { label: 'SHOPIFY', color: '#65A30D', emoji: '\uD83D\uDED2' },
  amazon_bsr: { label: 'AMAZON', color: '#EA580C', emoji: '\uD83D\uDCE6' },
  culture: { label: 'CULTURE', color: '#F43F5E', emoji: '\uD83C\uDF10' },
};

const CAT_FALLBACK_IMG = {
  technology: 'https://images.unsplash.com/photo-1518770660439-4636190af475?w=600&h=400&fit=crop',
  finance: 'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=600&h=400&fit=crop',
  world_news: 'https://images.unsplash.com/photo-1504711434969-e33886168d6c?w=600&h=400&fit=crop',
  politics: 'https://images.unsplash.com/photo-1529107386315-e1a2ed48a620?w=600&h=400&fit=crop',
  ai: 'https://images.unsplash.com/photo-1677212004257-103cfa6b59d0?w=600&h=400&fit=crop',
  entertainment: 'https://images.unsplash.com/photo-1626814026160-2237a95fc5a0?w=600&h=400&fit=crop',
  sports: 'https://images.unsplash.com/photo-1461896836934-bd45ba8a0daa?w=600&h=400&fit=crop',
  crypto: 'https://images.unsplash.com/photo-1639133893916-a711d8af8c0a?w=600&h=400&fit=crop',
  economy: 'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=600&h=400&fit=crop',
  lifestyle: 'https://images.unsplash.com/photo-1529156069898-49953e39b3ac?w=600&h=400&fit=crop',
  internet_culture: 'https://images.unsplash.com/photo-1611162617474-5b21e879e113?w=600&h=400&fit=crop',
  culture: 'https://images.unsplash.com/photo-1529156069898-49953e39b3ac?w=600&h=400&fit=crop',
};

// Filter out known brand logos and social media preview images that are irrelevant
const IMAGE_BLACKLIST = [
  'pbs.twimg.com/profile', 'abs.twimg.com', 'pbs.twimg.com/card_img',
  'logo', 'favicon', 'icon', 'avatar', 'badge', 'brand',
  'netflix.com', 'spotify.com', 'facebook.com', 'instagram.com',
];

function getValidImage(topic) {
  const url = topic.image_url;
  if (!url) return CAT_FALLBACK_IMG[topic.category] || null;
  const lower = url.toLowerCase();
  if (IMAGE_BLACKLIST.some(b => lower.includes(b))) return CAT_FALLBACK_IMG[topic.category] || null;
  // Reject tiny images (likely icons)
  if (lower.includes('32x32') || lower.includes('16x16') || lower.includes('48x48')) return CAT_FALLBACK_IMG[topic.category] || null;
  return url;
}

export default function ExplanationView({ topic, user, onBack, onShare, onNext, onPrev }) {
  const [explanation, setExplanation] = useState(null);
  const [overlay, setOverlay] = useState(null);
  const [premiumLocked, setPremiumLocked] = useState(false);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [progressStep, setProgressStep] = useState(0);

  const PROGRESS_STEPS = [
    { text: 'Finding sources...', pct: 15 },
    { text: 'Reading articles...', pct: 35 },
    { text: 'AI is analyzing...', pct: 55 },
    { text: 'Writing your cards...', pct: 75 },
    { text: 'Almost there...', pct: 90 },
  ];

  // Cycle through progress steps while generating
  useEffect(() => {
    if (!generating) { setProgressStep(0); return; }
    const interval = setInterval(() => {
      setProgressStep(prev => prev < PROGRESS_STEPS.length - 1 ? prev + 1 : prev);
    }, 800);
    return () => clearInterval(interval);
  }, [generating]);
  const [activeTab, setActiveTab] = useState('what');
  const [saved, setSaved] = useState(false);
  const [reaction, setReaction] = useState(null); // 'thumbsup' | 'thumbsdown' | null
  const touchRef = useRef({ startX: 0, startY: 0 });

  // Touch-based swipe for navigating between topics
  const handleTouchStart = useCallback((e) => {
    touchRef.current.startX = e.touches[0].clientX;
    touchRef.current.startY = e.touches[0].clientY;
  }, []);

  const handleTouchEnd = useCallback((e) => {
    const dx = e.changedTouches[0].clientX - touchRef.current.startX;
    const dy = e.changedTouches[0].clientY - touchRef.current.startY;
    // Only trigger if horizontal swipe is dominant and exceeds threshold
    if (Math.abs(dx) > 80 && Math.abs(dx) > Math.abs(dy) * 1.5) {
      if (dx > 0 && onPrev) onPrev();
      else if (dx < 0 && onNext) onNext();
    }
  }, [onNext, onPrev]);

  useEffect(() => {
    let cancelled = false;

    const fetchExplanation = async () => {
      try {
        setGenerating(true);
        const data = await api.getExplanation(topic.id);
        if (cancelled) return;
        setExplanation(data.explanation);
        setPremiumLocked(!!data.premium_locked);
      } catch (e) {
        console.error('Explanation load failed:', e);
      } finally {
        if (!cancelled) {
          setGenerating(false);
          setLoading(false);
        }
      }
    };

    const fetchOverlay = async () => {
      try {
        const data = await api.getOverlay(topic.id);
        if (cancelled) return;
        if (data.overlay && data.overlay.personal_angle) {
          setOverlay(data.overlay);
        }
      } catch (e) {
        // Overlay is optional, fail silently
      }
    };

    // Fetch both in parallel — explanation renders first, overlay fills in when ready
    fetchExplanation();
    fetchOverlay();
    return () => { cancelled = true; };
  }, [topic.id]);

  const handleSave = async () => {
    try {
      const result = await api.saveTopic(topic.id);
      setSaved(result.saved);
    } catch (e) {
      console.error('Save failed:', e);
    }
  };

  const handleShare = () => {
    if (onShare) onShare(topic);
  };

  const cat = CATEGORY_CONFIG[topic.category] || CATEGORY_CONFIG.world_news;
  const isHot = topic.trend_score >= 85;

  const cardContent = explanation ? {
    what: {
      main: explanation.card_1 || '',
      hook: explanation.card_1_hook || '',
      detail: explanation.card_1_detail || '',
    },
    why: {
      main: explanation.card_2 || '',
      hook: explanation.card_2_hook || '',
      detail: explanation.card_2_detail || '',
    },
    matters: {
      affects: explanation.card_3_affects || [],
      action: explanation.card_3_action || [],
      main: explanation.card_3 || '',
      hook: explanation.card_3_hook || '',
      detail: explanation.card_3_detail || '',
    },
  } : {};

  const colors = LABEL_COLORS[activeTab];

  return (
    <div
      data-testid="explanation-view"
      className="min-h-screen"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* Header bar */}
      <div className="px-5 pb-3" style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 20px)', background: `linear-gradient(180deg, ${cat.color}15 0%, transparent 100%)` }}>
        <div className="flex items-center justify-between mb-3">
          <button
            data-testid="back-button"
            onClick={onBack}
            className="w-10 h-10 rounded-full bg-white/10 border border-white/10 flex items-center justify-center
              hover:bg-white/20 transition-all z-[70]"
          >
            <ArrowLeft size={20} className="text-white" />
          </button>
          <div className="flex items-center gap-2">
            <button
              data-testid="save-button"
              onClick={handleSave}
              className={`w-9 h-9 rounded-full flex items-center justify-center transition-colors border border-white/10
                ${saved ? 'bg-yellow-500/20 text-yellow-400' : 'bg-white/10 text-white hover:bg-white/20'}`}
            >
              <Bookmark size={18} fill={saved ? 'currentColor' : 'none'} />
            </button>
            <button
              data-testid="share-button"
              onClick={handleShare}
              className="w-9 h-9 rounded-full bg-white/10 border border-white/10 flex items-center justify-center
                hover:bg-white/20 transition-colors"
            >
              <Share2 size={18} className="text-white" />
            </button>
          </div>
        </div>

        {/* Topic info */}
        <div className="flex items-center gap-2 mb-2">
          <span className="text-[10px] font-bold tracking-widest uppercase" style={{ color: cat.color }}>
            {cat.label}
          </span>
          <div className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold
            ${isHot ? 'bg-red-500/20 text-red-400' : 'bg-white/10 text-white/50'}`}>
            {isHot && <span className="w-1.5 h-1.5 rounded-full bg-red-500 pulse-dot" />}
            <span>🔥 {topic.trend_score}</span>
          </div>
        </div>
        <h2 className="text-xl font-bold text-white leading-snug">{topic.title}</h2>
        {/* Multi-source attribution */}
        {topic.sources && topic.sources.length > 1 && (
          <div data-testid="sources-attribution" className="flex items-center gap-1.5 mt-2 flex-wrap">
            <span className="text-[10px] font-semibold text-white/40 uppercase tracking-wider">Reported by</span>
            {topic.sources.map((s, i) => (
              <span key={i} className="inline-flex items-center px-2 py-0.5 rounded-full bg-white/10 text-[10px] font-bold text-white/60">
                {s.source.replace(/_/g, ' ')}
              </span>
            ))}
          </div>
        )}
        {topic.sources && topic.sources.length === 1 && (
          <p className="text-[10px] font-semibold text-white/40 uppercase tracking-wider mt-2">
            via {topic.sources[0].source.replace(/_/g, ' ')}
          </p>
        )}
      </div>

      {/* Card tabs */}
      <div className="flex justify-center gap-1 px-5 py-3">
        {CARD_TABS.map(tab => (
          <button
            key={tab.key}
            data-testid={`tab-${tab.key}`}
            onClick={() => setActiveTab(tab.key)}
            className={`px-5 py-2 rounded-full text-xs font-bold tracking-wider transition-all duration-200
              ${activeTab === tab.key
                ? 'bg-gray-900 text-white shadow-md'
                : 'bg-white/10 text-white/50 hover:bg-white/20'}`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Explanation card */}
      <div className="px-5 mt-1">
        {loading ? (
          <div data-testid="skeleton-loader" className="rounded-3xl p-6 min-h-[340px] flex flex-col overflow-hidden relative"
            style={{ background: 'linear-gradient(160deg, #1D4ED8 0%, #3B82F6 100%)' }}>
            {/* Shimmer overlay */}
            <div className="absolute inset-0 -translate-x-full animate-[shimmer_1.5s_ease-in-out_infinite]"
              style={{ background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.08) 50%, transparent 100%)' }} />

            {/* Topic title at top */}
            <p className="text-white/50 text-[11px] font-bold tracking-wider uppercase mb-6">
              {topic.title?.length > 50 ? topic.title.slice(0, 50) + '...' : topic.title}
            </p>

            {/* Centered progress section */}
            <div className="flex-1 flex flex-col items-center justify-center gap-5">
              {/* Animated spinner */}
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ repeat: Infinity, duration: 1.5, ease: 'linear' }}
                className="w-12 h-12 rounded-full border-[3px] border-white/15 border-t-white"
              />

              {/* Step text with fade transition */}
              <AnimatePresence mode="wait">
                <motion.p
                  key={progressStep}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.25 }}
                  className="text-sm font-semibold text-white/90"
                >
                  {PROGRESS_STEPS[progressStep]?.text || 'Loading...'}
                </motion.p>
              </AnimatePresence>

              {/* Progress bar */}
              <div className="w-48 h-1.5 rounded-full bg-white/15 overflow-hidden">
                <motion.div
                  className="h-full rounded-full bg-white/70"
                  initial={{ width: '5%' }}
                  animate={{ width: `${PROGRESS_STEPS[progressStep]?.pct || 10}%` }}
                  transition={{ duration: 0.6, ease: 'easeOut' }}
                />
              </div>
            </div>

            {/* Bottom hint */}
            <p className="text-[10px] text-white/40 text-center mt-4">
              Cards are cached for instant replay
            </p>
          </div>
        ) : explanation ? (
          <AnimatePresence mode="wait">
            {/* Premium lock overlay — ALL tabs locked for premium categories */}
            {premiumLocked ? (
              <motion.div
                key="premium-locked"
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -30 }}
                transition={{ duration: 0.25 }}
                data-testid="premium-lock-card"
                className="rounded-3xl min-h-[340px] flex flex-col items-center justify-center gap-4 p-8"
                style={{ background: 'linear-gradient(160deg, #1E1B4B 0%, #312E81 50%, #4338CA 100%)' }}
              >
                <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center mb-2">
                  <Sparkles size={28} className="text-amber-400" />
                </div>
                <h3 className="text-xl font-bold text-white text-center">
                  Premium Content
                </h3>
                <p className="text-sm text-white/60 text-center leading-relaxed max-w-[240px]">
                  Subscribe to unlock full explanations for premium categories like crypto, finance & trading
                </p>
                <button
                  data-testid="premium-unlock-btn"
                  className="mt-2 px-6 py-3 rounded-full bg-gradient-to-r from-amber-400 to-orange-500 text-sm font-bold text-white shadow-lg hover:shadow-xl transition-all"
                >
                  Unlock Premium
                </button>
              </motion.div>
            ) : (
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -30 }}
              transition={{ duration: 0.25 }}
              className="rounded-3xl overflow-hidden flex flex-col"
              style={{
                background: '#ffffff',
                paddingLeft: 6,
                paddingBottom: 7,
                boxShadow: '0 12px 40px rgba(0,0,0,0.18), 0 2px 8px rgba(0,0,0,0.1)',
              }}
            >
              {/* Inner card */}
              <div className={`${CARD_TABS.find(t => t.key === activeTab)?.className} 
                rounded-3xl flex-1 flex flex-col relative overflow-hidden`}
              >
              {/* Card layout: heading → explanation → image → source */}
              <div className="relative z-10 flex flex-col h-full">
                {/* Top section: heading + explanation text */}
                <div className="p-6 pb-4">
                  <h3 className="text-3xl font-black text-white uppercase tracking-wide mb-3">
                    {activeTab === 'what' ? 'What' : activeTab === 'why' ? 'Why' : 'You'}
                  </h3>
                  {/* Headline (one-liner) */}
                  <p className="text-base text-white font-bold leading-snug mb-2">
                    {activeTab === 'matters'
                      ? (cardContent.matters?.affects?.[0] || cardContent.matters?.main || '')
                      : activeTab === 'why'
                        ? (cardContent.why?.main || '')
                        : (cardContent.what?.main || '')}
                  </p>
                  {/* Hook + detail — gives the WHAT/WHY/YOU card real substance
                      (iter 61L). Renders only when content exists so empty
                      explanations don't show empty paragraphs. */}
                  {(() => {
                    const hook = activeTab === 'what' ? cardContent.what?.hook
                              : activeTab === 'why' ? cardContent.why?.hook
                              : cardContent.matters?.hook;
                    const detail = activeTab === 'what' ? cardContent.what?.detail
                                : activeTab === 'why' ? cardContent.why?.detail
                                : cardContent.matters?.detail;
                    return (
                      <>
                        {hook && (
                          <p className="text-sm text-white/85 italic leading-relaxed mb-2">
                            {hook}
                          </p>
                        )}
                        {detail && (
                          <p className="text-sm text-white/80 leading-relaxed">
                            {detail}
                          </p>
                        )}
                      </>
                    );
                  })()}
                </div>

                {/* Image — only on the WHAT tab (per user request: keeps WHY/YOU
                    compact and avoids visual repetition across tabs). Iter 61h:
                    bumped to 200px tall and switched to object-cover so the
                    image properly fills the card width. */}
                {activeTab === 'what' && (
                  <div className="relative mx-4 mb-3 rounded-2xl overflow-hidden"
                    style={{ height: 200, background: `linear-gradient(135deg, ${cat.color}33, ${cat.color}11)` }}>
                    {getValidImage(topic) ? (
                      <img
                        src={getValidImage(topic)}
                        alt=""
                        className="w-full h-full object-cover"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    ) : null}
                    {/* Emoji fallback behind image */}
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <span className="text-6xl opacity-15">{CATEGORY_CONFIG[topic.category]?.emoji || ''}</span>
                    </div>
                  </div>
                )}

                {/* Source link at bottom — bigger, bold, white */}
                <div className="px-6 pb-5 pt-2">
                  {topic.source_url ? (
                    <button
                      data-testid="source-link"
                      className="inline-flex items-center gap-2 text-base font-bold text-white hover:text-white/80 transition-colors"
                      onClick={(e) => {
                        e.stopPropagation();
                        openExternal(topic.source_url);
                      }}
                    >
                      <ExternalLink size={16} />
                      Source article
                    </button>
                  ) : (
                    <span className="text-base font-bold text-white/60">
                      Source article
                    </span>
                  )}
                </div>
              </div>
              </div>{/* end inner card */}
            </motion.div>
            )}
          </AnimatePresence>
        ) : (
          <div className="rounded-3xl h-[340px] flex items-center justify-center"
            style={{ background: 'linear-gradient(160deg, #374151 0%, #6B7280 100%)' }}>
            <p className="text-white/70">Failed to load explanation</p>
          </div>
        )}
      </div>

      {/* Progress dots + source attribution */}
      <div className="flex flex-col items-center mt-5 gap-2.5">
        <div className="flex gap-2">
          {CARD_TABS.map(tab => (
            <div
              key={tab.key}
              className={`h-1.5 rounded-full transition-all duration-300
                ${activeTab === tab.key ? 'w-8 bg-gray-900' : 'w-3 bg-gray-300'}`}
            />
          ))}
        </div>

        {/* Thumbs up / down */}
        <div className="flex items-center gap-4 mt-2" data-testid="topic-vote">
          <button
            data-testid="vote-thumbsup"
            onClick={async () => {
              const next = reaction === 'thumbsup' ? null : 'thumbsup';
              setReaction(next);
              try { await api.reactToTopic(topic.id, 'thumbsup'); } catch {}
            }}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold transition-all
              ${reaction === 'thumbsup'
                ? 'bg-green-100 text-green-700 ring-1 ring-green-300 scale-105'
                : 'bg-white/10 text-white/50 hover:bg-white/20'}`}
          >
            <ThumbsUp size={18} fill={reaction === 'thumbsup' ? 'currentColor' : 'none'} />
          </button>
          <button
            data-testid="vote-thumbsdown"
            onClick={async () => {
              const next = reaction === 'thumbsdown' ? null : 'thumbsdown';
              setReaction(next);
              try { await api.reactToTopic(topic.id, 'thumbsdown'); } catch {}
              // User-disliked topic should disappear from their feed (iter 61d).
              if (next === 'thumbsdown') {
                try { await api.dismissTopic(topic.id); } catch {}
                if (onBack) onBack();
              }
            }}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold transition-all
              ${reaction === 'thumbsdown'
                ? 'bg-red-100 text-red-600 ring-1 ring-red-300 scale-105'
                : 'bg-white/10 text-white/50 hover:bg-white/20'}`}
          >
            <ThumbsDown size={18} fill={reaction === 'thumbsdown' ? 'currentColor' : 'none'} />
          </button>
        </div>

        {/* Source link — spaced well below thumbs, big bold white */}
        <div className="mt-8 mb-4 px-4">
          <button
            data-testid="card-source-link"
            className="inline-flex items-center gap-2.5 text-lg font-black text-white hover:text-white/80 transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              const url = topic.source_url || (explanation?.sources?.[0]?.url) || (topic.sources?.[0]?.url);
              if (url) openExternal(url);
            }}
          >
            <ExternalLink size={20} />
            <span>Source article</span>
          </button>
        </div>
        {/* Fallback: raw_data link if no source URLs */}
        {(!topic.sources || !topic.sources.some(s => s.url)) && topic.raw_data?.link && (
          <div className="flex justify-center mt-2 px-4">
            <button
              type="button"
              data-testid="read-source-link"
              onClick={(e) => {
                e.stopPropagation();
                openExternal(topic.raw_data.link);
              }}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20 text-xs font-semibold text-white/60 transition-colors"
            >
              <ExternalLink size={11} />
              Source article
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
